import { api, ApiError, getPage } from "../api.js";
import { esc, displayName, cohortIcons, formatDate } from "../utils.js";
import { navigate } from "../app.js";
import { getCurrentUser } from "../auth.js";
import { infiniteScroll } from "../infiniteScroll.js";
import { openChatSocket } from "../chatSocket.js";
import { mountChatPanel, deletedLabel, typingText, attachmentSummary } from "../chatPanel.js";
import { setChatBadge } from "../chatBadge.js";

/**
 * Cohorts (#/cohorts, and #/cohort/<id>/chat for one cohort's chat): laid out
 * like Messenger. Your cohorts down the left, most recently active first, each
 * with its newest message and what you haven't read; the chosen one's chat on
 * the right. Each entry also links to the cohort's page. The search at the top
 * finds cohorts to join, shown where the chat is until it's cleared.
 *
 * One live connection for the page, watching every chat in the list, so the
 * list's previews and unread counts change as messages arrive, a preview says
 * "Ana is typing…" while someone is, and the navbar's Cohorts badge
 * (chatBadge.js) keeps up.
 *
 * On a narrow screen (a phone) one thing shows at a time, as in Messenger's
 * app: the list, then a chat filling the page with a back arrow, or the
 * search's results. #/cohorts is the list there, and opens no chat by itself.
 * A chat opened from the list is a step in the browser's history, so Back
 * returns to the list.
 */

const MINE_PAGE = 50;
const MINE_MAX_PAGES = 10;
/** How long "is typing" stays in the list without another sign of it. */
const TYPING_SHOWN_MS = 5000;

/** From this width the list and the chat sit side by side (Bootstrap's lg),
 *  matching the stylesheet's breakpoint for .dc-workspace. */
const WIDE = "(min-width: 992px)";

function wideMedia() {
  return typeof window.matchMedia === "function" ? window.matchMedia(WIDE) : null;
}

function wideScreen() {
  return wideMedia()?.matches ?? true;
}

/** The page on screen, so a new render (or leaving) can hang up the old one. */
let active = null;

function communityCardHtml(community) {
  const memberBadge = community.is_member
    ? `<i class="bi bi-check-circle-fill text-primary" title="You're a member"></i>`
    : "";
  return `
    <div class="card mb-3 card-dc-clickable" data-community-id="${community.id}">
      <div class="card-body d-flex">
        <div class="dc-avatar me-3" style="background-color:var(--dc-teal-100);">
          <i class="bi bi-people" style="color:var(--dc-teal-700);"></i>
        </div>
        <div class="flex-grow-1">
          <div class="d-flex justify-content-between align-items-start">
            <h3 class="h6 fw-semibold mb-0">${esc(community.name)}${community.is_private
              ? ` <span class="badge text-bg-light border ms-1" style="font-size:0.7rem;" title="Joined by invitation; content visible to members only"><i class="bi bi-lock me-1"></i>Private</span>`
              : ""}</h3>
            ${memberBadge}
          </div>
          ${community.description ? `<p class="text-slate small mb-2 mt-1 dc-user-text">${esc(community.description)}</p>` : ""}
          <div class="text-slate small"><i class="bi bi-people me-1"></i>${community.member_count ?? 0} members</div>
        </div>
      </div>
    </div>`;
}

/** The list's one line about a cohort's chat: "You: ...", "Ana: ...". */
export function previewText(cohort, meId) {
  const last = cohort.chat?.last_message;
  if (!last) return "No messages yet";
  if (last.is_deleted) return deletedLabel(last);
  const who = last.author?.id === meId ? "You" : displayName(last.author);
  // Photos or files alone: what they are.
  return `${who}: ${last.content || attachmentSummary(last.attachments)}`;
}

/** "now", "5m", "3h", "2d", then the date. */
export function shortTime(iso, now = Date.now()) {
  const seconds = Math.max(0, Math.floor((now - Date.parse(iso)) / 1000));
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d`;
  return formatDate(iso);
}

const activity = (cohort) => {
  const at = cohort.chat?.last_message?.created_at;
  return at ? Date.parse(at) : 0;
};

/** Most recently active first; quiet ones by name. */
export function byActivity(cohorts) {
  return [...cohorts].sort((a, b) => activity(b) - activity(a) || a.name.localeCompare(b.name));
}

async function loadMine() {
  const all = [];
  let cursor = null;
  for (let i = 0; i < MINE_MAX_PAGES; i += 1) {
    const { items, nextCursor } = await getPage("/cohorts", { query: { mine: 1 }, cursor, limit: MINE_PAGE });
    all.push(...items);
    cursor = nextCursor;
    if (!cursor) break;
  }
  return all;
}

export async function render(container, params) {
  active?.destroy();
  active = null;
  const me = getCurrentUser();
  const requested = params?.id ? Number(params.id) : null;

  container.innerHTML = `
    <div id="cohorts-page" data-view="list">
      <div class="d-flex align-items-center gap-3 mb-3 flex-wrap dc-cohorts-header">
        <h1 class="display-heading h3 mb-0">Cohorts</h1>
        <div class="flex-grow-1 dc-cohort-search">
          <input type="search" class="form-control" id="community-search" placeholder="Find cohorts to join..."
                 aria-label="Find cohorts to join" />
        </div>
        <button class="btn btn-accent" id="new-community-btn"><i class="bi bi-plus-lg me-1"></i>New</button>
      </div>
      <div class="dc-workspace">
        <aside class="card dc-cohort-sidebar" aria-label="Your cohorts">
          <div id="cohort-list"><div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div></div>
        </aside>
        <section class="card dc-cohort-main">
          <div id="chat-host"></div>
          <div class="d-none p-3" id="cohort-search-results">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="h6 mb-0" id="cohort-search-title"></h2>
              <button class="btn btn-sm btn-outline-secondary" type="button" id="cohort-search-close"
                      aria-label="Close the search"><i class="bi bi-x-lg"></i></button>
            </div>
            <div id="cohort-search-list"></div>
          </div>
        </section>
      </div>
    </div>`;

  const root = container.querySelector("#cohorts-page");
  const sidebar = container.querySelector("#cohort-list");
  const chatHost = container.querySelector("#chat-host");
  const results = container.querySelector("#cohort-search-results");
  const resultsTitle = container.querySelector("#cohort-search-title");
  const resultsList = container.querySelector("#cohort-search-list");
  const searchInput = container.querySelector("#community-search");

  let mine = [];
  let selectedId = null;
  let panel = null;
  let socket = null;
  let connectionStatus = "connecting";
  let searchFeed = null;
  let searchTimer = null;
  let refreshTimer = null;
  let destroyed = false;
  /** Whether the open chat was pushed onto the history from the list (narrow). */
  let pushedChat = false;
  /** chat id -> (user id -> { name, timer }): who is typing where. */
  const typing = new Map();

  /** "list", "chat" or "search": which one a narrow screen shows. Wide screens
   *  show the list beside whichever of the other two. */
  function setView(view) {
    root.dataset.view = view;
  }

  // ---- the list ----

  function renderSidebar() {
    if (!mine.length) {
      sidebar.innerHTML = `<p class="text-slate small p-3 mb-0" id="cohort-list-empty">You're not in any cohorts yet.
        Find one to join with the search above, or start one with New.</p>`;
      return;
    }
    // The navbar's badge counts the cohorts with something unread.
    setChatBadge(mine.filter((c) => (c.chat?.unread_count ?? 0) > 0).length);
    sidebar.innerHTML = `<div class="list-group list-group-flush">${mine.map((c) => {
      const unread = c.chat?.unread_count ?? 0;
      const last = c.chat?.last_message;
      const typists = [...(typing.get(c.chat?.id)?.values() ?? [])].map((t) => t.name);
      return `
        <div class="list-group-item d-flex align-items-center gap-2 dc-cohort-item ${c.id === selectedId ? "dc-selected" : ""}"
             data-cohort-id="${c.id}">
          <a href="#/cohort/${c.id}/chat" class="d-flex align-items-center gap-2 flex-grow-1 text-reset text-decoration-none dc-min-w-0"
             data-select="${c.id}" ${c.id === selectedId ? 'aria-current="true"' : ""}>
            <div class="dc-avatar flex-shrink-0"><i class="bi bi-people"></i></div>
            <div class="flex-grow-1 dc-min-w-0">
              <div class="d-flex justify-content-between align-items-baseline gap-2">
                <span class="${unread ? "fw-bold" : "fw-semibold"} dc-cohort-name">${cohortIcons(c)}${esc(c.name)}</span>
                ${last ? `<span class="small text-slate flex-shrink-0">${shortTime(last.created_at)}</span>` : ""}
              </div>
              <div class="d-flex justify-content-between align-items-center gap-2">
                ${typists.length
                  ? `<span class="small text-truncate fst-italic text-primary dc-cohort-preview dc-cohort-typing">${esc(typingText(typists))}</span>`
                  : `<span class="small text-truncate dc-cohort-preview ${unread ? "fw-semibold" : "text-slate"} ${
                      last?.is_deleted ? "fst-italic" : ""}">${esc(previewText(c, me?.id))}</span>`}
                ${unread ? `<span class="badge rounded-pill text-bg-danger flex-shrink-0 dc-cohort-unread"
                              aria-label="${unread} unread">${unread}</span>` : ""}
              </div>
            </div>
          </a>
          <a href="#/cohort/${c.id}" class="text-slate flex-shrink-0 dc-cohort-info" title="Open ${esc(c.name)}'s page"
             aria-label="Open ${esc(c.name)}'s page"><i class="bi bi-info-circle"></i></a>
        </div>`;
    }).join("")}</div>`;
  }

  function showTyping(chatId, user) {
    if (!user || user.id === me?.id || !cohortForChat(chatId)) return;
    const here = typing.get(chatId) ?? new Map();
    typing.set(chatId, here);
    clearTimeout(here.get(user.id)?.timer);
    here.set(user.id, {
      name: displayName(user),
      timer: setTimeout(() => stopTyping(chatId, user.id), TYPING_SHOWN_MS),
    });
    renderSidebar();
  }

  function stopTyping(chatId, userId) {
    const here = typing.get(chatId);
    const t = here?.get(userId);
    if (!t) return;
    clearTimeout(t.timer);
    here.delete(userId);
    if (!here.size) typing.delete(chatId);
    if (!destroyed) renderSidebar();
  }

  function cohortForChat(chatId) {
    return mine.find((c) => c.chat?.id === chatId);
  }

  function setUnread(chatId, count) {
    const cohort = cohortForChat(chatId);
    if (!cohort || cohort.chat.unread_count === count) return;
    cohort.chat.unread_count = count;
    renderSidebar();
  }

  async function refreshMine() {
    try {
      const fresh = await loadMine();
      if (destroyed) return;
      mine = byActivity(fresh);
      renderSidebar();
    } catch {
      // The list keeps what it had; the next message updates it anyway.
    }
  }

  sidebar.addEventListener("click", (e) => {
    const link = e.target.closest("[data-select]");
    // Middle-click and the like open a tab, as a link should.
    if (!link || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    select(Number(link.dataset.select), { fromList: true });
  });

  // ---- the chat ----

  async function select(cohortId, { fromList = false } = {}) {
    hideSearch();
    selectedId = cohortId;
    renderSidebar();
    // The address follows, without a hashchange (which would rebuild the page).
    // On a narrow screen, a chat opened from the list is a step back can undo.
    const address = `#/cohort/${cohortId}/chat`;
    if (fromList && !wideScreen() && !pushedChat) {
      history.pushState(null, "", address);
      pushedChat = true;
    } else {
      history.replaceState(null, "", address);
    }
    setView("chat");
    panel?.destroy();
    panel = null;

    let cohort = mine.find((c) => c.id === cohortId);
    if (!cohort) {
      try {
        cohort = await api.get(`/cohorts/${cohortId}`);
      } catch (err) {
        if (destroyed || selectedId !== cohortId) return;
        const notFound = err instanceof ApiError && err.status === 404;
        chatHost.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>${
          notFound ? "Cohort not found." : esc(err.message || "Could not load the chat.")}</div></div>`;
        return;
      }
    }
    if (destroyed || selectedId !== cohortId) return;
    const mounted = await mountChatPanel(chatHost, {
      cohort,
      socket,
      onRead: (chatId) => setUnread(chatId, 0),
      onBack: backToList,
    });
    // Someone chose another cohort, or left the page, while this one loaded.
    if (destroyed || selectedId !== cohortId) {
      mounted.destroy();
      return;
    }
    panel = mounted;
    panel.connection(connectionStatus);
    // A cohort reached by its address but not in the list (someone else's).
    if (cohort.chat) socket?.subscribe(cohort.chat.id);
  }

  /** The chat's back arrow (narrow screens). */
  function backToList() {
    if (pushedChat) {
      // As the browser's Back would: the list is the step before.
      history.back();
      return;
    }
    panel?.destroy();
    panel = null;
    selectedId = null;
    chatHost.innerHTML = "";
    history.replaceState(null, "", "#/cohorts");
    setView("list");
    renderSidebar();
  }

  /** A tablet turned, or a window resized, across the breakpoint. */
  function onWidthChange() {
    if (wideScreen() && selectedId == null && mine.length && root.dataset.view === "list") {
      select(mine[0].id);
    }
  }
  const media = wideMedia();
  media?.addEventListener?.("change", onWidthChange);

  // ---- the connection ----

  function onEvent(event) {
    const cohort = cohortForChat(event.chat_id);
    if (cohort && event.message && event.type.startsWith("message.")) {
      const last = cohort.chat.last_message;
      if (!last || event.message.id >= last.id) cohort.chat.last_message = event.message;
      if (event.type === "message.created" && event.message.author?.id !== me?.id) {
        cohort.chat.unread_count = (cohort.chat.unread_count ?? 0) + 1;
      }
      // Their message is what they were typing.
      if (event.type === "message.created") {
        const t = typing.get(event.chat_id)?.get(event.message.author?.id);
        if (t) {
          clearTimeout(t.timer);
          typing.get(event.chat_id).delete(event.message.author.id);
        }
      }
      mine = byActivity(mine);
      renderSidebar();
    } else if (event.type === "typing") {
      showTyping(event.chat_id, event.user);
    } else if (cohort && event.type === "read" && event.user?.id === me?.id && event.unread_count != null) {
      // Read in another tab or on another device.
      cohort.chat.unread_count = event.unread_count;
      renderSidebar();
    }
    // After the list, so a chat open at the bottom marks itself read again.
    panel?.handleEvent(event);
  }

  function onSubscribed(chatId, { again }) {
    if (panel?.chatId === chatId) panel.subscribed({ again });
    if (again) {
      // Back after a gap: the list's previews and counts may be stale. Once.
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refreshMine, 300);
    }
  }

  function onChatClosed(chatId, detail) {
    if (panel?.chatId === chatId) panel.closed(detail);
    if (cohortForChat(chatId)) {
      // No longer a member: it leaves the list.
      mine = mine.filter((c) => c.chat?.id !== chatId);
      renderSidebar();
    }
  }

  function onStatus(status) {
    connectionStatus = status;
    panel?.connection(status);
  }

  // ---- the search ----

  function showSearch(q) {
    searchFeed?.destroy();
    setView("search");
    chatHost.classList.add("d-none");
    results.classList.remove("d-none");
    resultsTitle.textContent = q ? `Cohorts matching “${q}”` : "Find a cohort to join";
    searchFeed = infiniteScroll(resultsList, {
      path: "/cohorts",
      query: q ? { q } : {},
      emptyHtml: `<div class="dc-empty"><i class="bi bi-people"></i><div class="fw-semibold">No cohorts found</div><div>Start one for your dive buddies with New.</div></div>`,
      onError: (err) => {
        resultsList.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>${esc(err.message || "Could not load cohorts.")}</div></div>`;
      },
      render: (list, communities) => {
        const batch = document.createElement("div");
        batch.innerHTML = communities.map(communityCardHtml).join("");
        batch.querySelectorAll("[data-community-id]").forEach((card) => {
          card.addEventListener("click", () => navigate(`/cohort/${card.dataset.communityId}`));
        });
        list.append(batch);
      },
    });
  }

  function hideSearch() {
    searchFeed?.destroy();
    searchFeed = null;
    results.classList.add("d-none");
    chatHost.classList.remove("d-none");
    setView(selectedId != null ? "chat" : "list");
  }

  searchInput.addEventListener("input", () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      const q = searchInput.value.trim();
      if (q) showSearch(q);
      else if (mine.length || selectedId) hideSearch();
      else showSearch("");
    }, 300);
  });

  container.querySelector("#cohort-search-close").addEventListener("click", () => {
    searchInput.value = "";
    if (mine.length || selectedId) hideSearch();
  });

  container.querySelector("#new-community-btn").addEventListener("click", () => navigate("/create-cohort"));

  // ---- hanging up ----

  const onLeave = () => {
    if (!container.contains(root)) destroy();
  };
  window.addEventListener("hashchange", onLeave);

  function destroy() {
    destroyed = true;
    clearTimeout(searchTimer);
    clearTimeout(refreshTimer);
    for (const here of typing.values()) for (const t of here.values()) clearTimeout(t.timer);
    typing.clear();
    socket?.close();
    panel?.destroy();
    searchFeed?.destroy();
    window.removeEventListener("hashchange", onLeave);
    media?.removeEventListener?.("change", onWidthChange);
    if (active?.destroy === destroy) active = null;
  }
  active = { destroy };

  // ---- loading ----

  try {
    mine = byActivity(await loadMine());
  } catch (err) {
    if (destroyed) return;
    sidebar.innerHTML = `<p class="text-danger small p-3 mb-0">${esc(err.message || "Could not load your cohorts.")}</p>`;
    mine = [];
  }
  if (destroyed) return;
  renderSidebar();

  socket = openChatSocket({ onEvent, onSubscribed, onChatClosed, onStatus });
  for (const c of mine) if (c.chat) socket.subscribe(c.chat.id);

  if (requested) select(requested);
  // A wide screen opens the most recent chat beside the list; a narrow one shows
  // just the list until a cohort is chosen.
  else if (mine.length && wideScreen()) select(mine[0].id);
  else if (mine.length) setView("list");
  else showSearch("");
}
